<script setup lang="ts">
import { computed, reactive, ref, watch } from "vue";
import Drawer from "primevue/drawer";
import Button from "primevue/button";
import Panel from "primevue/panel";
import Tag from "primevue/tag";
import Divider from "primevue/divider";
import Textarea from "primevue/textarea";
import InputText from "primevue/inputtext";
import Select from "primevue/select";
import Message from "primevue/message";
import { useToast } from "primevue/usetoast";

import EventDetail from "./EventDetail.vue";
import Markdown from "./Markdown.vue";
import SeverityBadge from "./SeverityBadge.vue";
import { isCtrlEnter } from "../keys";
import { reviewUrl } from "../deeplink";
import {
  addEventToReview,
  approveReview,
  deleteEvent,
  deleteReview,
  eventFinalSeverity,
  triggerLabel,
  updateEvent,
  updateReview,
  type CreateEventPayload,
  type EventPatchPayload,
  type QualityEvent,
  type Review,
  type Severity,
} from "../api";

const props = defineProps<{
  review: Review | null;
  visible: boolean;
  email: string | null;
  isManager: boolean;
  /**
   * Why the review couldn't be loaded, when the drawer was opened by URL and
   * the fetch failed (no access / no such review). Rendered in place of the
   * body; `review` is null in that case.
   */
  loadError?: string | null;
}>();

const emit = defineEmits<{
  (e: "update:visible", v: boolean): void;
  (e: "refresh", reviewId: number): void;
  (e: "deleted", reviewId: number): void;
}>();

const toast = useToast();

// --- View / edit mode state ------------------------------------------------

type EditableSeverity = Exclude<Severity, "none">;

interface QEEdit {
  id: number | null;          // null = new (no server id yet)
  clientKey: string;          // stable v-for key
  baseline: QualityEvent | null;  // snapshot at edit-start; null when isNew
  isNew: boolean;
  pendingDelete: boolean;
  // Mutable form fields
  category: "process_deviation" | "technical_quality_issue";
  severity: EditableSeverity;
  owner: string;
  summary: string;
  description: string;
  policyDocument: string;
  policySection: string;
  contributors: string;       // one email per line
}

interface Draft {
  fullText: string;
  fullTextBaseline: string;
  events: QEEdit[];
}

const editing = ref(false);
const saving = ref(false);
const editError = ref<string | null>(null);
const draft = reactive<Draft>({
  fullText: "",
  fullTextBaseline: "",
  events: [],
});

let clientKeyCounter = 0;
function nextClientKey(): string {
  clientKeyCounter += 1;
  return `qe-client-${clientKeyCounter}`;
}

const categoryOptions = [
  { label: "Process deviation", value: "process_deviation" },
  { label: "Technical quality issue", value: "technical_quality_issue" },
];
const severityOptions: { label: string; value: EditableSeverity }[] = [
  { label: "low", value: "low" },
  { label: "medium", value: "medium" },
  { label: "high", value: "high" },
  { label: "critical", value: "critical" },
];

function qeFromEvent(e: QualityEvent): QEEdit {
  // `Severity` includes "none" but the edit form forbids it — the backend
  // never assigns "none" to an event-level severity, so the cast is safe.
  return {
    id: e.id,
    clientKey: nextClientKey(),
    baseline: e,
    isNew: false,
    pendingDelete: false,
    category: e.category as QEEdit["category"],
    severity: e.severity as EditableSeverity,
    owner: e.owner,
    summary: e.summary,
    description: e.description,
    policyDocument: e.policy_document ?? "",
    policySection: e.policy_section ?? "",
    contributors: e.contributors.map((c) => c.contributor).join("\n"),
  };
}

function blankQE(): QEEdit {
  return {
    id: null,
    clientKey: nextClientKey(),
    baseline: null,
    isNew: true,
    pendingDelete: false,
    category: "technical_quality_issue",
    severity: "medium",
    owner: "",
    summary: "",
    description: "",
    policyDocument: "",
    policySection: "",
    contributors: "",
  };
}

function enterEdit(): void {
  if (!props.review) return;
  cancelDelete(); // don't leave a stale delete-confirm hanging during edit
  draft.fullText = props.review.full_text;
  draft.fullTextBaseline = props.review.full_text;
  draft.events = props.review.events.map(qeFromEvent);
  editError.value = null;
  editing.value = true;
}

function cancelEdit(): void {
  editing.value = false;
  editError.value = null;
  draft.fullText = "";
  draft.fullTextBaseline = "";
  draft.events = [];
}

function addQE(): void {
  draft.events.push(blankQE());
}

function toggleDelete(qe: QEEdit): void {
  if (qe.isNew) {
    // Newly-added rows are discarded outright on delete — there's nothing
    // on the server to mark.
    draft.events = draft.events.filter((x) => x.clientKey !== qe.clientKey);
    return;
  }
  qe.pendingDelete = !qe.pendingDelete;
}

// Close the drawer if the user closes it while editing — discard the draft.
watch(
  () => props.visible,
  (v) => {
    if (!v) {
      if (editing.value) cancelEdit();
      cancelDelete();
    }
  },
);

// Reset the delete confirm step whenever the drawer switches to another review.
watch(
  () => props.review?.id,
  () => cancelDelete(),
);

// --- Save flow --------------------------------------------------------------

function parseContributors(raw: string): { contributor: string }[] {
  return raw
    .split(/[\n,]+/)
    .map((s) => s.trim())
    .filter((s) => s.length > 0)
    .map((c) => ({ contributor: c }));
}

function validateForSave(): string | null {
  const live = draft.events.filter((e) => !e.pendingDelete);
  for (const e of live) {
    if (!e.summary.trim()) return `A QE summary is required (QE-${e.id ?? "new"}).`;
    if (e.summary.includes("\n") || e.summary.includes("\r")) {
      return `QE summary must be a single line (QE-${e.id ?? "new"}).`;
    }
    if (!e.owner.trim()) return `QE owner is required (QE-${e.id ?? "new"}).`;
    if (!e.description.trim()) {
      return `QE description is required (QE-${e.id ?? "new"}).`;
    }
  }
  return null;
}

function diffEventPatch(qe: QEEdit): EventPatchPayload | null {
  if (!qe.baseline) return null;
  const base = qe.baseline;
  const patch: EventPatchPayload = {};
  if (qe.category !== base.category) patch.category = qe.category;
  if (qe.severity !== base.severity) patch.severity = qe.severity;
  if (qe.owner.trim() !== base.owner) patch.owner = qe.owner.trim();
  if (qe.summary.trim() !== base.summary) patch.summary = qe.summary.trim();
  if (qe.description !== base.description) patch.description = qe.description;
  const pd = qe.policyDocument.trim() || null;
  if (pd !== (base.policy_document ?? null)) patch.policy_document = pd;
  const ps = qe.policySection.trim() || null;
  if (ps !== (base.policy_section ?? null)) patch.policy_section = ps;
  const newContribs = parseContributors(qe.contributors).map((c) => c.contributor);
  const oldContribs = base.contributors.map((c) => c.contributor);
  if (
    newContribs.length !== oldContribs.length ||
    newContribs.some((c, i) => c !== oldContribs[i])
  ) {
    patch.contributors = newContribs.map((c) => ({ contributor: c }));
  }
  return Object.keys(patch).length === 0 ? null : patch;
}

async function saveEdit(): Promise<void> {
  if (!props.review) return;
  editError.value = null;
  const validationError = validateForSave();
  if (validationError) {
    editError.value = validationError;
    return;
  }
  const reviewId = props.review.id;
  saving.value = true;
  try {
    // 1. Review body (if changed).
    if (draft.fullText !== draft.fullTextBaseline) {
      await updateReview(reviewId, { full_text: draft.fullText });
    }

    // 2. Existing events that were edited.
    for (const qe of draft.events) {
      if (qe.isNew || qe.pendingDelete) continue;
      const patch = diffEventPatch(qe);
      if (patch && qe.id !== null) {
        await updateEvent(qe.id, patch);
      }
    }

    // 3. Deletions of existing events.
    for (const qe of draft.events) {
      if (!qe.isNew && qe.pendingDelete && qe.id !== null) {
        await deleteEvent(qe.id);
      }
    }

    // 4. New events.
    for (const qe of draft.events) {
      if (!qe.isNew) continue;
      const payload: CreateEventPayload = {
        date: new Date().toISOString(),
        category: qe.category,
        severity: qe.severity,
        owner: qe.owner.trim(),
        summary: qe.summary.trim(),
        description: qe.description,
        contributors: parseContributors(qe.contributors),
        policy_document: qe.policyDocument.trim() || null,
        policy_section: qe.policySection.trim() || null,
      };
      await addEventToReview(reviewId, payload);
    }

    toast.add({
      severity: "success",
      summary: "Saved",
      detail: "Curation applied. The review is still unapproved.",
      life: 3000,
    });
    editing.value = false;
    emit("refresh", reviewId);
  } catch (e) {
    // Fail-fast: keep the user in edit mode with their draft intact so they
    // can retry or cancel. Server-side state may be partially updated; the
    // next refresh will reflect it.
    editError.value = String(e);
    toast.add({
      severity: "error",
      summary: "Save failed",
      detail: String(e),
      life: 6000,
    });
  } finally {
    saving.value = false;
  }
}

// --- View-mode helpers (unchanged from before) -----------------------------

function fmt(d: string | null): string {
  if (!d) return "—";
  return new Date(d).toLocaleString();
}

async function onApprove(): Promise<void> {
  if (!props.review) return;
  try {
    await approveReview(props.review.id);
    toast.add({
      severity: "success",
      summary: "Review approved",
      detail: "Contributors have been notified.",
      life: 3000,
    });
    emit("refresh", props.review.id);
  } catch (e) {
    toast.add({
      severity: "error",
      summary: "Could not approve",
      detail: String(e),
      life: 5000,
    });
  }
}

function onVisibleUpdate(v: boolean): void {
  emit("update:visible", v);
}

// --- shareable link ---------------------------------------------------------
// The dashboard keeps `/#/reviews/:id` in the address bar for whichever review
// is open, so this is just that URL made grabbable in one click. Anyone can
// copy it; the backend decides whether the person you send it to may open it.

const shareUrl = computed<string>(() =>
  props.review ? reviewUrl(window.location.origin, props.review.id) : "",
);

async function copyLink(): Promise<void> {
  if (!shareUrl.value) return;
  try {
    await navigator.clipboard.writeText(shareUrl.value);
    toast.add({
      severity: "success",
      summary: "Link copied",
      detail: shareUrl.value,
      life: 3000,
    });
  } catch {
    // Clipboard access is denied outside a secure context (plain-HTTP dev
    // hosts other than localhost). Show the URL so it can be copied by hand.
    toast.add({
      severity: "info",
      summary: "Copy this link",
      detail: shareUrl.value,
      life: 10000,
    });
  }
}

function onEventChanged(): void {
  if (props.review) emit("refresh", props.review.id);
}

const drawerHeader = computed<string>(() => {
  if (!props.review) return "Review";
  return (
    props.review.issue_title ||
    props.review.issue_code ||
    `review ${props.review.id}`
  );
});

const drawerSubheader = computed<string>(() => {
  if (!props.review) return "";
  const r = props.review;
  const bits: string[] = [];
  if (r.repo) bits.push(r.repo);
  if (r.issue_code && r.issue_code !== drawerHeader.value) bits.push(r.issue_code);
  if (r.pr_number) bits.push(`PR #${r.pr_number}`);
  return bits.join(" · ");
});

const canEdit = computed<boolean>(
  () => props.isManager && !!props.review && props.review.approved_by === null,
);

// --- Delete flow (idle → confirming → deleting) ----------------------------
// Mirrors the confirm-step pattern used for contributor/owner actions in
// EventDetail. Same gate as editing: manager + unapproved review.

type DeleteStage = "idle" | "confirming" | "deleting";
const deleteStage = ref<DeleteStage>("idle");
const deleteError = ref<string | null>(null);

function startDelete(): void {
  deleteError.value = null;
  deleteStage.value = "confirming";
}

function cancelDelete(): void {
  deleteStage.value = "idle";
  deleteError.value = null;
}

async function confirmDelete(): Promise<void> {
  if (!props.review) return;
  const reviewId = props.review.id;
  deleteStage.value = "deleting";
  deleteError.value = null;
  try {
    await deleteReview(reviewId);
    toast.add({
      severity: "success",
      summary: "Review deleted",
      detail: `Review R-${reviewId} and its quality events were deleted.`,
      life: 3000,
    });
    deleteStage.value = "idle";
    emit("deleted", reviewId);
    emit("update:visible", false);
  } catch (e) {
    // Stay on the confirm step with the error so the manager can retry/cancel.
    deleteStage.value = "confirming";
    deleteError.value = String(e);
  }
}
</script>

<template>
  <Drawer
    :visible="visible"
    @update:visible="onVisibleUpdate"
    position="right"
    :style="{ width: 'min(720px, 95vw)' }"
  >
    <template #header>
      <div class="drawer-header">
        <div class="drawer-title">
          <span v-if="review" class="drawer-id" data-testid="review-id-tag">
            R-{{ review.id }}
          </span>
          <span class="drawer-title-text">{{ drawerHeader }}</span>
          <Tag
            v-if="editing"
            value="editing"
            severity="info"
            data-testid="edit-mode-tag"
          />
        </div>
        <div v-if="drawerSubheader" class="drawer-sub">{{ drawerSubheader }}</div>
      </div>
    </template>

    <Message
      v-if="!review && loadError"
      severity="warn"
      :closable="false"
      data-testid="review-load-error"
    >
      {{ loadError }}
    </Message>
    <div v-else-if="!review">No review selected.</div>
    <div
      v-else
      class="drawer-body"
      @keydown="(e: KeyboardEvent) => editing && !saving && isCtrlEnter(e) && saveEdit()"
    >
      <div class="head-band">
        <SeverityBadge
          :severity="review.highest_severity_event"
          :final-severity="review.final_highest_severity_event"
          prefix="Severity"
        />
        <Tag :value="triggerLabel(review.trigger_type)" severity="secondary" />
        <span class="head-spacer" />
        <Button
          label="Copy link"
          icon="pi pi-link"
          severity="secondary"
          text
          size="small"
          data-testid="copy-link-btn"
          @click="copyLink"
        />
      </div>

      <dl class="meta">
        <div><dt>Date</dt><dd>{{ fmt(review.date) }}</dd></div>
        <div>
          <dt>Author</dt>
          <dd>
            {{ review.author_email || review.author_system || "unknown" }}
            <span v-if="!review.author_is_human" class="muted"> (automated)</span>
          </dd>
        </div>
        <div v-if="review.author_instructions">
          <dt>Reviewer instructions</dt>
          <dd><code>{{ review.author_instructions }}</code></dd>
        </div>
        <div v-if="review.commit_id">
          <dt>Commit</dt>
          <dd><code>{{ review.commit_id }}</code></dd>
        </div>
        <div>
          <dt>Approval</dt>
          <dd v-if="review.approved_by">
            <i class="pi pi-check-circle" style="color: var(--p-green-500)"></i>
            {{ review.approved_by }} <span class="muted">·</span>
            {{ fmt(review.approved_date) }}
          </dd>
          <dd v-else>
            <span class="muted">Not yet approved.</span>
            <span v-if="canEdit && !editing" class="approval-actions">
              <Button
                label="Edit"
                icon="pi pi-pencil"
                severity="secondary"
                size="small"
                data-testid="edit-btn"
                @click="enterEdit"
              />
              <Button
                label="Approve now"
                icon="pi pi-check"
                size="small"
                :data-testid="'approve-btn-' + review.id"
                @click="onApprove"
              />
              <Button
                label="Delete"
                icon="pi pi-trash"
                severity="danger"
                outlined
                size="small"
                :data-testid="'delete-btn-' + review.id"
                @click="startDelete"
              />
            </span>
          </dd>
        </div>
        <div v-if="review.edited_by">
          <dt>Last edited</dt>
          <dd>
            {{ review.edited_by }} <span class="muted">·</span>
            {{ fmt(review.edited_at) }}
          </dd>
        </div>
      </dl>

      <Message
        v-if="canEdit && deleteStage !== 'idle'"
        severity="warn"
        :closable="false"
        data-testid="delete-confirm"
      >
        <div class="delete-confirm">
          <p class="delete-confirm-text">
            Delete review <strong>R-{{ review.id }}</strong><template
              v-if="review.events.length"
            > and its {{ review.events.length }} quality
              event{{ review.events.length === 1 ? "" : "s" }}</template>?
            This cannot be undone.
          </p>
          <p v-if="deleteError" class="delete-error" data-testid="delete-error">
            {{ deleteError }}
          </p>
          <div class="delete-confirm-actions">
            <Button
              label="Cancel"
              severity="secondary"
              text
              size="small"
              :disabled="deleteStage === 'deleting'"
              data-testid="delete-cancel"
              @click="cancelDelete"
            />
            <Button
              label="Delete review"
              icon="pi pi-trash"
              severity="danger"
              size="small"
              :loading="deleteStage === 'deleting'"
              :data-testid="'delete-confirm-btn-' + review.id"
              @click="confirmDelete"
            />
          </div>
        </div>
      </Message>

      <Divider />

      <h3 class="section-h">Review</h3>
      <div v-if="!editing">
        <Markdown :source="review.full_text" />
      </div>
      <div v-else>
        <Textarea
          v-model="draft.fullText"
          rows="10"
          autoResize
          class="full-text-edit"
          data-testid="review-body-edit"
        />
      </div>

      <div class="events">
        <div class="events-header">
          <h3 class="section-h">Quality events</h3>
          <Button
            v-if="editing"
            label="Add QE"
            icon="pi pi-plus"
            size="small"
            severity="secondary"
            data-testid="add-qe-btn"
            @click="addQE"
          />
        </div>

        <!-- View mode: render existing QEs via the normal EventDetail panel. -->
        <template v-if="!editing">
          <Panel
            v-for="e in review.events"
            :key="e.id"
            :toggleable="true"
            :collapsed="false"
            class="event-panel"
          >
            <template #header>
              <div class="panel-head">
                <SeverityBadge
                  :severity="e.severity"
                  :final-severity="eventFinalSeverity(e)"
                />
                <Tag
                  :value="e.status"
                  :severity="
                    e.status === 'closed' ? 'success' :
                    e.status === 'disputed' ? 'danger' :
                    'secondary'
                  "
                />
                <span class="panel-id" :data-testid="'qe-id-' + e.id">QE-{{ e.id }}</span>
                <span class="panel-summary">{{ e.summary }}</span>
              </div>
            </template>
            <EventDetail
              :event="e"
              :email="email"
              :is-manager="isManager"
              @changed="onEventChanged"
            />
          </Panel>
          <p v-if="!review.events.length" class="muted">
            This review has no quality events attached.
          </p>
        </template>

        <!-- Edit mode: render every QE as an inline form. -->
        <template v-else>
          <Panel
            v-for="qe in draft.events"
            :key="qe.clientKey"
            :toggleable="true"
            :collapsed="false"
            class="event-panel"
            :class="{ 'qe-deleted': qe.pendingDelete }"
          >
            <template #header>
              <div class="panel-head">
                <span v-if="qe.isNew" class="panel-id panel-id-new">NEW</span>
                <span v-else class="panel-id">QE-{{ qe.id }}</span>
                <span v-if="qe.pendingDelete" class="panel-summary muted">
                  Marked for deletion
                </span>
                <span v-else class="panel-summary">
                  {{ qe.summary || "(no summary yet)" }}
                </span>
                <span class="panel-spacer" />
                <Button
                  :icon="qe.pendingDelete ? 'pi pi-undo' : 'pi pi-trash'"
                  severity="danger"
                  text
                  rounded
                  size="small"
                  :data-testid="'qe-delete-' + (qe.id ?? qe.clientKey)"
                  :aria-label="qe.pendingDelete ? 'Undo delete' : 'Delete QE'"
                  @click.stop="toggleDelete(qe)"
                />
              </div>
            </template>

            <div v-if="qe.pendingDelete" class="qe-deleted-body">
              <Message severity="warn" :closable="false" size="small">
                This QE will be deleted on Save. Click the undo icon to keep it.
              </Message>
            </div>
            <form v-else class="form" @submit.prevent>
              <div class="row">
                <div class="field">
                  <label>Category</label>
                  <Select
                    v-model="qe.category"
                    :options="categoryOptions"
                    optionLabel="label"
                    optionValue="value"
                  />
                </div>
                <div class="field">
                  <label>Severity</label>
                  <Select
                    v-model="qe.severity"
                    :options="severityOptions"
                    optionLabel="label"
                    optionValue="value"
                  />
                </div>
              </div>
              <div class="field">
                <label>Summary (one line)</label>
                <InputText v-model="qe.summary" :maxlength="255" />
              </div>
              <div class="field">
                <label>Owner email</label>
                <InputText v-model="qe.owner" />
              </div>
              <div class="field">
                <label>Description</label>
                <Textarea v-model="qe.description" rows="5" autoResize />
              </div>
              <div class="field">
                <label>
                  Contributors
                  <span class="muted">(one email per line, or comma-separated)</span>
                </label>
                <Textarea v-model="qe.contributors" rows="3" autoResize />
              </div>
              <div class="row">
                <div class="field">
                  <label>Policy document <span class="muted">(optional)</span></label>
                  <InputText v-model="qe.policyDocument" />
                </div>
                <div class="field">
                  <label>Policy section <span class="muted">(optional)</span></label>
                  <InputText v-model="qe.policySection" />
                </div>
              </div>
            </form>
          </Panel>
          <p v-if="!draft.events.length" class="muted">
            No QEs yet. Click "Add QE" to add one.
          </p>
        </template>
      </div>

      <Message
        v-if="editing && editError"
        severity="error"
        :closable="false"
        size="small"
      >
        {{ editError }}
      </Message>

      <div v-if="editing" class="edit-footer">
        <Button
          label="Cancel"
          severity="secondary"
          :disabled="saving"
          @click="cancelEdit"
        />
        <Button
          label="Save"
          icon="pi pi-save"
          :loading="saving"
          data-testid="save-edit-btn"
          @click="saveEdit"
        />
      </div>
    </div>
  </Drawer>
</template>

<style scoped>
.drawer-header {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}
.drawer-title {
  display: flex;
  align-items: baseline;
  gap: 8px;
  font-weight: 700;
  font-size: 16px;
  min-width: 0;
}
.drawer-title-text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
}
.drawer-id {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 12px;
  font-weight: 600;
  color: var(--p-surface-600);
  background: var(--p-surface-100);
  padding: 2px 8px;
  border-radius: 4px;
  flex-shrink: 0;
  letter-spacing: 0.02em;
}
.drawer-sub {
  font-size: 12px;
  color: var(--p-surface-500);
}
.drawer-body {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.head-band {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  align-items: center;
}
.head-spacer { flex: 1; }
.section-h {
  font-size: 12px;
  font-weight: 600;
  margin: 8px 0 6px;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--p-surface-500);
}
.muted { color: var(--p-surface-500); }
dl.meta {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 6px 16px;
  margin: 4px 0;
  font-size: 13px;
}
dl.meta div { display: flex; gap: 6px; align-items: baseline; }
dl.meta dt { color: var(--p-surface-500); font-weight: 500; flex-shrink: 0; }
dl.meta dd { margin: 0; }
.approval-actions { display: inline-flex; gap: 8px; margin-left: 8px; flex-wrap: wrap; }
.delete-confirm { display: flex; flex-direction: column; gap: 8px; }
.delete-confirm-text { margin: 0; }
.delete-error { margin: 0; color: var(--p-red-600, #dc2626); font-size: 13px; }
.delete-confirm-actions { display: flex; gap: 8px; justify-content: flex-end; }
.events { display: flex; flex-direction: column; gap: 12px; }
.events-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.events-header .section-h { margin: 0; }
.panel-head {
  display: flex;
  gap: 6px;
  align-items: center;
  flex: 1;
}
.panel-spacer { flex: 1; }
.panel-id {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 11px;
  font-weight: 600;
  color: var(--p-surface-600);
  background: var(--p-surface-0, white);
  padding: 1px 6px;
  border-radius: 3px;
  border: 1px solid var(--p-surface-200);
  letter-spacing: 0.02em;
}
.panel-id-new {
  background: var(--p-blue-50, #eff6ff);
  border-color: var(--p-blue-300, #93c5fd);
  color: var(--p-blue-700, #1d4ed8);
}
.panel-summary { font-weight: 600; margin-left: 4px; }
.qe-deleted .panel-summary { text-decoration: line-through; }
.qe-deleted-body { padding: 4px 0; }
.full-text-edit { width: 100%; font-family: inherit; font-size: 14px; }
.form { display: flex; flex-direction: column; gap: 12px; }
.row { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
.field { display: flex; flex-direction: column; gap: 4px; }
.field label {
  font-size: 12px;
  font-weight: 500;
  color: var(--p-surface-700);
}
.edit-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  position: sticky;
  bottom: 0;
  background: var(--p-surface-0, white);
  padding: 12px 0 4px;
  border-top: 1px solid var(--p-surface-200);
  margin-top: 8px;
}
:deep(.p-panel-content) { padding: 14px 18px; }
:deep(.p-panel-header) { padding: 10px 14px; background: var(--p-surface-50); }
</style>
